import React from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const ConfigDebugView = ({
  excludedKeywords,
  includedKeywords,
  language,
  writingStyle,
  selectedFeatures,
  titlePrompt,
  titleCustomPrompt,
  titleIncludeVariants,
  descriptionPrompt,
  descriptionCustomPrompt,
  descriptionKeepOriginal,
  descriptionIncludeTitle,
  altTextPrompt,
  altTextCustomPrompt,
  seoPrompt,
  seoCustomPrompt,
  tagsPrompt,
  tagsCustomPrompt,
  marketRadarGeo,
  imagefinderGeo,
  selectedFormat
}: {
  excludedKeywords: string[],
  includedKeywords: string[],
  language: string,
  writingStyle: string,
  selectedFeatures: {
    title?: boolean,
    description?: boolean,
    altText?: boolean,
    seo?: boolean,
    marketRadar?: boolean,
    productTags?: boolean,
    image_finder?: boolean // Add this to type
  },
  titlePrompt: string,
  titleCustomPrompt: string,
  titleIncludeVariants: boolean,
  descriptionPrompt: string,
  descriptionCustomPrompt: string,
  descriptionKeepOriginal: boolean,
  descriptionIncludeTitle: boolean,
  altTextPrompt: string,
  altTextCustomPrompt: string,
  seoPrompt: string,
  seoCustomPrompt: string,
  tagsPrompt: string,
  tagsCustomPrompt: string,
  marketRadarGeo: string,
  imagefinderGeo: string,
  selectedFormat: string 
}) => {
  // Debug state info
  const stateInfo = {
    title: {
      promptType: titlePrompt,
      customPrompt: titleCustomPrompt,
      finalPrompt: titlePrompt === "custom" ? titleCustomPrompt : titlePrompt,
    },
    description: {
      promptType: descriptionPrompt,
      customPrompt: descriptionCustomPrompt,
      finalPrompt: descriptionPrompt === "custom" ? descriptionCustomPrompt : descriptionPrompt,
    },
    seo: {
      promptType: seoPrompt,
      customPrompt: seoCustomPrompt,
      finalPrompt: seoPrompt === "custom" ? seoCustomPrompt : seoPrompt,
    },
    altText: {
      promptType: altTextPrompt,
      customPrompt: altTextCustomPrompt,
      finalPrompt: altTextPrompt === "custom" ? altTextCustomPrompt : altTextPrompt,
    },
    productTags: {
      promptType: tagsPrompt,
      customPrompt: tagsCustomPrompt,
      finalPrompt: tagsPrompt === "custom" ? tagsCustomPrompt : tagsPrompt,
    },
    marketRadar: {
      geo: marketRadarGeo
    },
    image_finder: {
      geo: imagefinderGeo  // Add image finder state info
    }
  };

  // Final config object
  const config = {
    general: {
      exclude_keywords: excludedKeywords,
      include_keywords: includedKeywords,
      language: language,
      writing_style: writingStyle,
      format: selectedFormat,
    },
    title: {
      generate: selectedFeatures.title ?? false,
      include_variant_info: titleIncludeVariants,
      prompt: titlePrompt === "custom" ? titleCustomPrompt : titlePrompt,
    },
    description: {
      generate: selectedFeatures.description ?? false,
      keep_original: descriptionKeepOriginal,
      include_product_title: descriptionIncludeTitle,
      prompt: descriptionPrompt === "custom" ? descriptionCustomPrompt : descriptionPrompt,
    },
    alt_text: {
      generate: selectedFeatures.altText ?? false,
      prompt: altTextPrompt === "custom" ? altTextCustomPrompt : altTextPrompt,
    },
    seo: {
      generate: selectedFeatures.seo ?? false,
      prompt: seoPrompt === "custom" ? seoCustomPrompt : seoPrompt,
    },
    market_radar: {
      generate: selectedFeatures.marketRadar ?? false,
      geo: marketRadarGeo
    },
    product_tags: {
      generate: selectedFeatures.productTags ?? false,
      prompt: tagsPrompt === "custom" ? tagsCustomPrompt : tagsPrompt,
    },
    image_finder: {  // Add image finder config
      generate: selectedFeatures.image_finder ?? false,
      geo: imagefinderGeo
    }
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-lg font-medium flex items-center gap-2">
          Debug View
        </CardTitle>
        <CardDescription>
          View current state and final config
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="state">
          <TabsList>
            <TabsTrigger value="state">State</TabsTrigger>
            <TabsTrigger value="config">Config</TabsTrigger>
          </TabsList>
          
          <TabsContent value="state">
            <div className="bg-gray-50 p-4 rounded-lg">
              <pre className="overflow-auto max-h-96">
                {JSON.stringify({
                  features: selectedFeatures,
                  prompts: stateInfo,
                  settings: {
                    language,
                    writingStyle,
                    excludedKeywords,
                    includedKeywords,
                    marketRadarGeo,
                    imagefinderGeo,
                    selectedFormat
                  }
                }, null, 2)}
              </pre>
            </div>
          </TabsContent>

          <TabsContent value="config">
            <div className="bg-gray-50 p-4 rounded-lg">
              <pre className="overflow-auto max-h-96">
                {JSON.stringify(config, null, 2)}
              </pre>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ConfigDebugView;